import useLocalStore from "../store/useLocalStore";
import {useMemo} from "react";
import {CEXType, ISocialLoginMethod, LoginMethodType} from "../types/types";
import {
    getAuthInfoApi,
    getEmailCodeApi,
    getOverviewInfoApi,
    tgAppLoginInitApi,
    toLogoutApi, userInviteApi,
    verifyEmailCodeApi
} from "../api";
import {isSuccess} from "../api/request";
import {useMatch} from "../MatchContext";
import eventManager from "./eventManager";
import {encodeBase64, isInTgApp} from "../utils";
import matchlog from "../utils/matchlog";
import {
    useBTCModalStore,
    useCEXBindModalStore,
    useEVMModalStore,
    useTONModalStore,
    useTRONModalStore, useWalletModalStore
} from "../store/useModalStore";
import {LOGIN_METHOD_MAP} from "../config";
import useStore from "../store/useStore";
import {useDownMd} from "./useLayout";

export default function useUserInfo() {
    const {
        appid,
        token,
        mid,
        did,
        logout: logoutStore,
        setOverview,
        overview,
        address,
        endpoints,
        locale,
        refreshOverview,
    } = useLocalStore();
    const {setTgAppAuthCode,emailLoginKey,setEmailLoginKey} = useStore()
    const isDownMd = useDownMd()
    const {events, login} = useMatch()
    const {open:EVMOpen} = useEVMModalStore()
    const {open: TRONOpen} = useTRONModalStore()
    const {open: TONOpen} = useTONModalStore()
    const {open: BTCOpen} = useBTCModalStore()
    const {open: CEXBindOpen} = useCEXBindModalStore()
    const walletModalStore = useWalletModalStore()

    const getRedirectUri = ()=>{
        return encodeURIComponent(window.location.href)
    }


    const isLogin = useMemo(() => !!token && !!overview, [token, overview])

    const logout = async () => {
        try {
            await toLogoutApi()
        } catch (err: any) {
            console.error('logout', err)
        }

        logoutStore()
        events && events!.onLogout && events!.onLogout()
        eventManager.emit('onLogout');
    }

    const loginByMethod = async (method: ISocialLoginMethod) => {
        const link = `${endpoints.back}api/v1/auth/${method == 'youtube' ? 'google' : method}?appid=${appid}&provider=${method == 'youtube' ? 'google' : method}&redirect=${encodeURIComponent(endpoints.auth + 'login/' + method)}&authorization=${new Date().getTime()}${method == "youtube" ? '&auth_type=youtube' : ''}`;
        matchlog.log('link', link)
        return window.open(
            link,  // Replace with the actual authorization URL
            'authWindow',
            'width=800,height=600'
        );
    }

    const loginByTwitter = () => {
        return loginByMethod('twitter')
    }
    const loginByGoogle = () => {
        return loginByMethod('google')
    }

    const loginByWallet = async () => {
        // return EVMOpen('login')
        return window.open(`${endpoints.auth}login/wallet?appid=${appid}&locale=${locale}`)

    }
    const loginByTelegram = async () => {
        if (isInTgApp()) {
            //get auth code
            try {
                const res = await tgAppLoginInitApi()
                if (!isSuccess(res)) {
                    throw new Error(res.message)
                }
                setTgAppAuthCode(res.data.code)
                const link = res.data.login_url + `?startapp=${res.data.code}_${appid}_${locale}`
                console.log('tgAppLoginInitLink', link)
                if (window.TelegramWebviewProxy) {
                    console.log('in telegram webview')
                    if (!window.Telegram) {
                        throw new Error('Please import telegram-web-app.js first')
                    }
                    window.Telegram.WebApp.openTelegramLink(link)
                    return
                }
                console.log('not in telegram webview')
                window.open(link)
                return


            } catch (e: any) {
                console.error('tgAppLoginInitApi', e)
                throw e
            }
        }

        return window.open(
            `${endpoints.auth}login/telegram?appid=${appid}&locale=${locale}`,  // Replace with the actual authorization URL
            'authWindow',
            'width=800,height=600'
        );
    }

    const loginMethod = async (method: LoginMethodType | 'wallet', extra?: {
        [key: string]: any
    }) => {
        switch (method) {
            case "wallet":
                return walletModalStore.open("login", extra?.methods)
            case "evm":
                return EVMOpen('login')
                // return window.open(`${endpoints.auth}login/wallet?appid=${appid}&locale=${locale}`)
            case "sol":
                if(isDownMd){
                    return window.location.href = `${endpoints.auth}login/sol?appid=${appid}&locale=${locale}&redirect_uri=${getRedirectUri()}`
                }
                return window.open(`${endpoints.auth}login/sol?appid=${appid}&locale=${locale}`)
            case "tron":
                return TRONOpen('login')
            case "ton":
                return TONOpen('login')
            case "btc":
                return BTCOpen('login')
            case "telegram":
                return await loginByTelegram()
            case "youtube":
            case "twitter":
            case "google":
            case "discord":
            case "github":
            case "linkedin":
            case "facebook":
                return await loginByMethod(method)
            default:
                throw new Error('unsupported method')

        }
    }


    const getLoginEmailCode = async (email: string) => {
        const res = await getEmailCodeApi(email);
        if (isSuccess(res)) {
            setEmailLoginKey(res.data.key)
            // window.sessionStorage.setItem('loginkey', res.data.key as string)
            return res.data.key as string
        }
        throw new Error(res.message)
    }

    const loginByEmail = async ({
                                    email,
                                    code
                                }: { email: string, code: string }) => {
        try {
            const obj = {
                email: email,
                verification_key: emailLoginKey,
                verification_code: code,
            }
            const res = await verifyEmailCodeApi(obj);
            if (res && res.data && isSuccess(res)) {
                await login({
                    // mid: res.data.mid,
                    token: `${res.data.token_type} ${res.data.access_token}`
                })
                return true;

            } else {
                throw new Error(res.message)
            }
        } catch (error) {
            console.error('loginByEmail', error);
            throw error
        }
        return false
    };


    const bindWallet = async () => {
        if (!token) {
            throw new Error('You must login first')
        }
        return window.open(`${endpoints.auth}bind/wallet?appid=${appid}&locale=${locale}&st=` + encodeURIComponent(encodeBase64(token)))
    }

    const bindTelegram = async () => {
        if (!token) {
            throw new Error('You must login first')
        }
        const authWindow = window.open(
            `${endpoints.auth}bind/telegram?appid=${appid}&locale=${locale}&st=` + encodeURIComponent(encodeBase64(token)),  // Replace with the actual authorization URL
            'authWindow',
            'width=800,height=600'
        );
    }

    const auth = async () => {
        const res = await getAuthInfoApi()
        if (isSuccess(res)) {
            return res.data
        }
        throw new Error(res.message)
    }

    const bind = async (method: LoginMethodType | 'wallet', extra?: {
        [key: string]: any
    }) => {
        if (!token) {
            throw new Error('You must login first')
        }
        switch (method) {
            case "wallet":
                return walletModalStore.open("bind", extra?.methods)
            case "evm":
                return EVMOpen('bind')
                // return window.open(`${endpoints.auth}bind/wallet?appid=${appid}&locale=${locale}&st=` + encodeURIComponent(encodeBase64(token)))
            case "sol":
                if(isDownMd){
                    return window.location.href = `${endpoints.auth}bind/sol?appid=${appid}&locale=${locale}&st=${encodeURIComponent(encodeBase64(token))}&redirect_uri=${getRedirectUri()}`
                }
                return window.open(`${endpoints.auth}bind/sol?appid=${appid}&locale=${locale}&st=${encodeURIComponent(encodeBase64(token))}`)
            case "tron":
                return TRONOpen('bind')
            case "ton":
                return TONOpen('bind')
            case "btc":
                return BTCOpen('bind')
            case "telegram":
                return await bindTelegram()
            case "twitter":
            case "google":
            case "discord":
            case "github":
            case "linkedin":
            case "facebook":
            case "youtube":
                const link = `${endpoints.back}api/v1/auth/${method == 'youtube' ? 'google' : method}?appid=${appid}&provider=${method == 'youtube' ? 'google' : method}&redirect=${encodeURIComponent(endpoints.auth + 'bind/' + method)}&authorization=${token.includes("Bearer ") ? token.split(' ')[1] : token}${method == "youtube" ? '&auth_type=youtube' : ''}`;
                matchlog.log('link', link)
                return window.open(
                    link,
                    'authWindow',
                    'width=800,height=600'
                );
            default:
                throw new Error('unsupported method')

        }
    }

    const bindCex = (type: CEXType) => {
        return CEXBindOpen(type)
    }

    const getAuthInfo = async (method: keyof typeof LOGIN_METHOD_MAP) => {
        // create a full screen iframe to get the auth info
        const element_id = 'auth_iframe'
        //if the iframe already exists, remove it
        if (document.getElementById(element_id)) {
            document.getElementById(element_id)!.remove()
        }
        const iframe = document.createElement('iframe')
        iframe.id = element_id
        iframe.src = `${endpoints.auth}auth?appid=${appid}&method=${method}&locale=${locale}&token=${encodeURIComponent(encodeBase64(token))}`
        iframe.style.width = '100vw'
        iframe.style.height = '100vh'
        iframe.style.position = 'fixed'
        iframe.style.top = '0'
        iframe.style.left = '0'
        iframe.style.zIndex = '9999'
        iframe.style.border = 'none'
        //@ts-ignore
        iframe.allowTransparency = 'true'
        document.body.appendChild(iframe)
        return new Promise((resolve, reject) => {
            const authMessageHandle = (event: MessageEvent) => {
                if (endpoints.auth.includes(event.origin)) {
                    const res = event.data
                    if (res.source == 'match-auth') {
                        window.removeEventListener('message', authMessageHandle)
                        if (res.status == 'success') {
                            resolve({
                                ...res.data,
                                method: res.method,
                                appid: res.appid,
                                did
                            })
                        } else {
                            reject(new Error(res.message))
                        }

                        iframe.remove()
                    }
                }
            }
            window.addEventListener('message', authMessageHandle)
        })
    }

    const invite = async(invite_code:string)=>{
        return await userInviteApi({
            invite_code
        })
    }


    return {
        loginByMethod,
        loginByTelegram,
        loginByTwitter,
        loginByGoogle,
        loginByWallet,
        loginByEmail,

        bindWallet,
        bindTelegram,


        token,
        mid,
        did,
        address,
        isLogin,
        logout,
        getLoginEmailCode,
        refreshOverview,
        overview,
        bindCex,
        username: overview?.username || '',
        auth,

        login: loginMethod,
        bind,

        getAuthInfo,
        invite
    }
}